import BottomSheet from '@components/BottomSheet'
import ErrorPopup from '@components/ErrorPopup'
import SwapConfig from '@components/SwapConfig'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { useBackAfterSheet } from '@hooks/useBackAfterSheet'
import { CKBTC_ICP_ID, ODINBTC_ID } from '@plug-core/constants/tokenIds'
import useConfig from '@plug-core/hooks/useConfig'
import useSwap from '@plug-core/hooks/useSwap'
import { Chain } from '@plug-core/interfaces/common'
import { SwapProvider as Providers } from '@plug-core/interfaces/swapInterface'
import { SwapProvider, useSwapContext } from '@plug-core/providers/SwapProvider'
import { Button, Dialog, IconButton } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiArrowUpRight } from 'react-icons/hi2'
import { VscSettings } from 'react-icons/vsc'
import { useLocation, useNavigate } from 'react-router-dom'
import SwapReview from './Components/SwapReview'
import ExchangeView from './ExchangeView'

const SwapTokenSelect = () => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const navigate = useNavigate()

  const {
    fromToken,
    toToken,
    setFromToken,
    setToToken,
    amount,
    setAmount,
    toAmount,
    setToAmount,
    swapReviewVisible,
    setSwapReviewVisible,
    swapConfigVisible,
    setSwapConfigVisible,
    setLockedToToken,
  } = useSwapContext()

  const lockToToken: boolean = !!state?.lockToToken
  const lockFromToken: boolean = !!state?.lockFromToken
  const sourceTokenIds: string[] | undefined = state?.sourceTokenIds

  useEffect(() => {
    setLockedToToken(lockToToken)
  }, [lockToToken])

  const {
    performSwap,
    currentSwapStep,
    currentQuote,
    swapError,
    swapExecutionError,
    setExecutionSwapError,
    priceHuman,
    maxAmount,
    formattedFee,
    priceDifference,
  } = useSwap({ fromTokenId: state?.fromTokenId, toTokenId: state?.toTokenId })

  const { tokens: allTokens } = useConfig()

  const sourceTokens = sourceTokenIds
    ? allTokens.filter((t) => sourceTokenIds.includes(t.id))
    : undefined

  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const onOdinBtcInfoClick = () => {
    setPrompt({
      title: t(`swap.odin.btcBalanceTitle`),
      description: t(`swap.odin.btcBalanceDescription`),
    })
  }

  const onErrorClose = () => {
    setSwapReviewVisible(false)
    setExecutionSwapError(undefined)
  }

  const handleSubmit = useCallback(() => setSwapReviewVisible(true), [setSwapReviewVisible])

  const onError = (error: string) => {
    setSwapReviewVisible(false)
    setExecutionSwapError(error)
  }

  const backAfterSheet = useBackAfterSheet()

  const onSuccess = () => {
    setSwapReviewVisible(false)
    backAfterSheet(t('swap.completed'))
  }

  return (
    <div className="mb-3 flex flex-col">
      <Titlebar
        onActionClick={() => navigate(-1)}
        leftItem={
          <IconButton
            aria-label={t('swap.config')}
            onClick={() => setSwapConfigVisible(true)}
            className="text-accent"
          >
            <VscSettings />
          </IconButton>
        }
      />

      <ExchangeView
        setFromToken={(id) => setFromToken(allTokens.find((t) => t.id === id))}
        toToken={toToken}
        setToToken={(id) => setToToken(allTokens.find((t) => t.id === id))}
        fromToken={fromToken}
        amount={amount}
        setAmount={setAmount}
        maxAmount={maxAmount}
        toAmount={toAmount}
        setToAmount={setToAmount}
        priceHuman={priceHuman}
        error={swapError}
        currentQuote={currentQuote}
        tokens={allTokens}
        nextTitle={t('swap.reviewSwap')}
        handleSubmit={handleSubmit}
        formattedFee={formattedFee}
        priceDifference={priceDifference}
        lockToToken={lockToToken}
        lockFromToken={lockFromToken}
        sourceTokens={sourceTokens}
      >
        <BottomSheet
          visible={swapReviewVisible}
          onBackdropClick={() => setSwapReviewVisible(false)}
        >
          <SwapReview
            performSwap={performSwap}
            currentStep={currentSwapStep}
            formattedFee={formattedFee}
            onError={onError}
            onSuccess={onSuccess}
            priceDifference={priceDifference}
          />
        </BottomSheet>
        {currentQuote?.provider === Providers.ICPSWAP && (
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate(ROUTES.SWAP_FALLBACK_PROVIDER_SELECT)}
            className="self-center"
          >
            {t('swap.fallback')}
          </Button>
        )}
        {fromToken?.chain === Chain.ODIN && (
          <Button
            variant="link"
            startIcon={<HiArrowUpRight />}
            onClick={() => onOdinBtcInfoClick()}
            fullWidth
            size="sm"
          >
            {t('swap.odin.btcBalanceTitle')}
          </Button>
        )}
        <SwapConfig visible={swapConfigVisible} close={() => setSwapConfigVisible(false)} />
      </ExchangeView>
      <ErrorPopup
        visible={!!swapExecutionError}
        title="Swap Failed"
        description={swapExecutionError?.toString()}
        onDismiss={onErrorClose}
      />
      <Dialog
        open={!!prompt}
        onClose={() => setPrompt(null)}
        title={prompt?.title}
        description={<span className="whitespace-pre-line">{prompt?.description}</span>}
        actions={
          <Button
            onClick={() => {
              const ckBtc = allTokens.find((t) => t.id === CKBTC_ICP_ID)
              const odinBtc = allTokens.find((t) => t.id === ODINBTC_ID)

              setFromToken(ckBtc)
              setToToken(odinBtc)
              setPrompt(null)
            }}
            variant="secondary"
            fullWidth
          >
            {t('swap.odin.bridge')}
          </Button>
        }
      />
    </div>
  )
}

export const Swap = () => (
  <SwapProvider>
    <SwapTokenSelect />
  </SwapProvider>
)
